// src/components/Dashboard/filterFields.tsx
//
// The controls both Outsourcing filter bars are made of.
//
// InquiryFilterBar and RequisitionFilterBar render the same four shapes - a
// dropdown with an "All" entry, a numeric id field, a date field, and the
// clear button - and wrote each of them out separately. They had already
// drifted: the inquiry bar debounced its id fields and capped their length,
// the requisition bar did neither, so Sales Order ID fired a request per
// keystroke and could send a value long enough to 500 the endpoint. Sharing
// the controls is what stops that happening again.
import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, MenuItem, SxProps, TextField, Theme } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

const FIELD_SX: SxProps<Theme> = { minWidth: 160, background: "var(--white)" };

/** The row the controls sit in: wrapping, evenly gapped, vertically centred. */
export const FilterRow: FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "0.75rem",
      alignItems: "center",
    }}
  >
    {children}
  </div>
);

type Option = { code: string; label: string };

type FilterSelectProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  // Code-keyed throughout: render the label, filter by the code. Undefined
  // while the options request is still out, which renders "All" alone rather
  // than an empty dropdown.
  options: Option[] | undefined;
  allLabel?: string;
  sx?: SxProps<Theme>;
};

export const FilterSelect: FunctionComponent<FilterSelectProps> = ({
  label,
  value,
  onChange,
  options,
  allLabel = "All",
  sx,
}) => (
  <TextField
    select
    size="small"
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    sx={{ ...FIELD_SX, ...sx }}
  >
    <MenuItem value="">{allLabel}</MenuItem>
    {options?.map((option) => (
      <MenuItem key={option.code} value={option.code}>
        {option.label}
      </MenuItem>
    ))}
  </TextField>
);

/** A list of plain strings, where the code and the label are the same word. */
export const asOptions = (values: readonly string[]): Option[] =>
  values.map((value) => ({ code: value, label: value }));

export const FilterDateField: FunctionComponent<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => (
  <TextField
    size="small"
    type="date"
    label={label}
    InputLabelProps={{ shrink: true }}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    sx={FIELD_SX}
  />
);

// Every commit here becomes a new filter string and therefore a request, so a
// free-typed id waits for a pause instead of firing per keystroke. Shorter
// than SearchField's 2000 ms: an id is a handful of digits, and the dropdowns
// around it apply instantly.
const ID_DEBOUNCE_MS = 400;

// ORDERS.id / QUOTES.id / REQUISITIONS.sales_order_id are 32-bit signed
// integers. Anything longer reaches the backend as `order_id=<20 digits>`, the
// comparison against an Integer column raises out-of-range, and the endpoint
// answers 500 in text/plain.
const ID_MAX_DIGITS = 9;

type FilterIdFieldProps = {
  label: string;
  /** The committed value. The input runs ahead of it while typing. */
  value: string;
  onChange: (value: string) => void;
  minWidth?: number;
};

/**
 * A digits-only id field that commits on a pause. Digits are stripped and
 * capped as they are typed, so the input can never hold something the backend
 * would choke on.
 */
export const FilterIdField: FunctionComponent<FilterIdFieldProps> = ({
  label,
  value,
  onChange,
  minWidth = 140,
}) => {
  const [text, setText] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // Someone else changed the value - Clear filters, or a restore from session
  // storage. Drop any pending commit (it would type the old digits back in)
  // and show what the filter actually holds.
  useEffect(() => {
    clearTimeout(timer.current);
    setText(value);
  }, [value]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <TextField
      size="small"
      label={label}
      value={text}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, ID_MAX_DIGITS);
        setText(digits);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => onChange(digits), ID_DEBOUNCE_MS);
      }}
      inputProps={{ maxLength: ID_MAX_DIGITS, inputMode: "numeric" }}
      sx={{ minWidth, background: "var(--white)" }}
    />
  );
};

/**
 * Always mounted, disabled when there is nothing to clear (EE-238): as the
 * last child of a wrapping row, mounting it could push the wrap boundary and
 * shift every control the user was aiming at.
 */
export const ClearFiltersButton: FunctionComponent<{
  disabled: boolean;
  onClick: () => void;
}> = ({ disabled, onClick }) => (
  <Button size="small" startIcon={<ClearIcon />} disabled={disabled} onClick={onClick}>
    Clear filters
  </Button>
);
